import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAgent } from '../App';
import { desktopApi } from '../lib/desktopApi';

const LoginPage = () => {
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [establishment, setEstablishment] = useState({
    name: 'Vatel Security',
    subtitle: 'Hôtel Vatel - Martigny',
    logo: '/gk-logo.png',
    hero: '/hotel-vatel.jpg',
  });
  const navigate = useNavigate();
  const { login, currentAgent } = useAgent();

  useEffect(() => {
    if (currentAgent) {
      navigate('/dashboard');
    }
    fetchAgents();
  }, [currentAgent, navigate]);

  const fetchAgents = async () => {
    try {
      const [nameRes, subtitleRes, logoRes, heroRes] = await Promise.all([
        desktopApi.getSetting('establishment_name').catch(() => null),
        desktopApi.getSetting('establishment_subtitle').catch(() => null),
        desktopApi.getSetting('logo_path').catch(() => null),
        desktopApi.getSetting('hero_image_path').catch(() => null),
      ]);
      setEstablishment(prev => ({
        name: nameRes?.value || prev.name,
        subtitle: subtitleRes?.value || prev.subtitle,
        logo: logoRes?.value || prev.logo,
        hero: heroRes?.value || prev.hero,
      }));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setLoginError('');
    try {
      const result = await desktopApi.loginAgent(username.trim(), password);
      login(result.agent, result.token);
      toast.success(`Bienvenue ${result.agent?.name || ''}`);
      navigate('/dashboard');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Identifiants incorrects');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="safe-screen h-[calc(100vh-2.25rem)] flex items-center justify-center bg-background">
        <div className="text-primary text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="safe-screen grid grid-cols-1 lg:grid-cols-2 h-[100dvh] lg:h-[calc(100vh-2.25rem)] w-full overflow-hidden">
      {/* Left Panel - Image */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-zinc-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-zinc-900/60 mix-blend-multiply z-10"></div>
        <img
          src={establishment.hero}
          alt={establishment.subtitle}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20">
          <div className="flex items-center gap-3 mb-4">
            <img src={establishment.logo} alt={establishment.name} className="w-16 h-16 rounded-lg" />
            <div>
              <h1 className="text-3xl font-black tracking-tight uppercase">{establishment.name}</h1>
              <p className="text-zinc-300 text-sm">Système de rapport de nuit</p>
            </div>
          </div>
        </div>
        <div className="relative z-20">
          <p className="text-zinc-400 text-sm">
            {establishment.subtitle}<br />
            Rapports de sécurité digitalisés
          </p>
        </div>
      </div>

      {/* Right Panel - Agent Selection (scrolls on its own) */}
      <div className="flex flex-col h-full min-h-0 bg-background">
        <div className="lg:hidden flex items-center gap-3 px-5 pt-6">
          <img src={establishment.logo} alt="" className="w-12 h-12 rounded-lg" />
          <div>
            <p className="text-lg font-black uppercase leading-none">{establishment.name}</p>
            <p className="text-xs text-muted-foreground">{establishment.subtitle}</p>
          </div>
        </div>
        <div className="shrink-0 px-5 md:px-8 pt-6 md:pt-10 pb-4">
          <div className="w-full max-w-md mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-foreground mb-2">
              Connexion
            </h2>
            <p className="text-muted-foreground">
              Identifiant et mot de passe remis par le responsable
            </p>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-5 md:px-8">
          <div className="w-full max-w-md mx-auto my-auto grid grid-cols-1 gap-3 py-2">
            <form onSubmit={submitPassword} className="space-y-4" data-testid="password-login">
                <label className="block text-sm font-medium">
                  Identifiant
                  <input
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    className="mt-1 h-12 w-full rounded-md border border-input bg-transparent px-3 text-base"
                    data-testid="login-username"
                    required
                  />
                </label>
                <label className="block text-sm font-medium">
                  Mot de passe
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="mt-1 h-12 w-full rounded-md border border-input bg-transparent px-3 text-base"
                    data-testid="login-password"
                    required
                  />
                </label>
                {loginError && <p className="text-sm text-red-300" data-testid="login-error">{loginError}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-12 w-full rounded-md bg-primary font-semibold text-primary-foreground disabled:opacity-50"
                  data-testid="login-submit"
                >
                  {submitting ? 'Connexion...' : 'Se connecter'}
                </button>
              </form>
          </div>
        </div>

        <div className="shrink-0 border-t border-border/30 px-8 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))]">
          <div className="w-full max-w-md mx-auto text-center">
            <button
              onClick={() => navigate('/admin')}
              data-testid="admin-link"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Accès Administrateur
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;